/**
 * @fileoverview Route guard for pages that require a signed-in session.
 * @description Blocks access when there's no access token and sends the visitor to /login instead,
 * and sends a signed-in user whose session is restricted (mustChangePassword) to /change-password —
 * a restricted session can't use any dashboard API anyway (403 / 1043), so the dashboard shell
 * (header, sidebar, profile/settings menu) is never shown until the password is changed. Used as a
 * layout route in the router so every nested route it wraps gets the same check. There is
 * no per-resource access-level gating here — the identity backend's privileges are real admin
 * capabilities (users:*, roles:*, tenants:*, etc.), not a mapping onto this blueprint's demo pages,
 * so a page that needs to gate on a specific privilege should check `user.privileges` itself rather
 * than this guard inventing a resource model that doesn't exist server-side.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { Navigate, Outlet } from 'react-router'

import { ROUTES } from '@/shared/constants/routes.constants'
import { useAuth } from '@/shared/lib/auth/useAuth'

/**
 * @component
 */
function ProtectedRoute() {
  const { isAuthenticated, user } = useAuth()

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />
  }

  if (user?.mustChangePassword) {
    return <Navigate to={ROUTES.CHANGE_PASSWORD} replace />
  }

  return <Outlet />
}

export default ProtectedRoute
